import { PureComponent } from 'react'
import { Link as RouterLink } from 'react-router-dom-v5-compat'

import Box from '@mui/material/Box'
import LinearProgress from '@mui/material/LinearProgress'
import Paper from '@mui/material/Paper'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import MuiTableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TablePagination from '@mui/material/TablePagination'
import TableRow from '@mui/material/TableRow'
import TextField from '@mui/material/TextField'
import Toolbar from '@mui/material/Toolbar'
import Typography from '@mui/material/Typography'
import { grey, orange, red } from '@mui/material/colors'
import { styled } from '@mui/material/styles'
import moment from 'moment'

import DateSelectButton from '~/oakra/components/date-select-button'
import { debounce } from '~/tools/utils'
import { getPath } from '~/utils'

const TableCell = styled(MuiTableCell)({
    padding: '10px 34px',
    wordWrap: 'break-word',
})

const Link = styled(RouterLink)({
    textDecoration: 'none',
    color: 'inherit',
})

const STATUS_INDICATOR_COLORS = {
    confirming: grey[300],
    ready_to_pick: grey[500],
    in_progress: orange[300],
    completed: '#41C49C',
    error: red[400],
}

const columns = [
    { id: 'id', label: gettext('ID'), centerAlign: false },
    { id: 'location', label: gettext('Location'), centerAlign: false },
    { id: 'date_created', label: gettext('Date'), centerAlign: false },
    { id: 'channel_names', label: gettext('Channels'), centerAlign: false },
    { id: 'num_fulfillments', label: gettext('Orders'), centerAlign: false },
    { id: 'num_items', label: gettext('Items'), centerAlign: false },
    { id: 'status', label: gettext('Status'), centerAlign: false },
]

function PickPackBatchesTableHead() {
    return (
        <TableHead sx={{ borderTop: (theme) => `1px solid ${theme.palette.grey[200]}` }}>
            <TableRow>
                {columns.map((column) => {
                    return (
                        <TableCell
                            key={column.id}
                            padding={column.disablePadding ? 'none' : 'default'}
                            sx={{
                                textAlign: column.centerAlign && 'center',
                                width: column.width ? column.width : 'auto',
                                minWidth: column.minWidth ? column.minWidth : 0,
                            }}
                        >
                            {column.label}
                        </TableCell>
                    )
                }, this)}
            </TableRow>
        </TableHead>
    )
}

function PickPackBatchesTableToolbar({
    handleChangePage,
    page,
    count,
    orderIdValueHandler,
    pickPackBatchIdValueHandler,
    searchHandler,
    filterValues,
    loading,
    onDateTimeChange,
}) {
    return (
        <Toolbar sx={{ pr: 1, display: 'flex', alignItems: 'center', minHeight: 68 }}>
            {loading && (
                <LinearProgress
                    sx={{ position: 'absolute', left: 0, right: 0, top: 0 }}
                    color="secondary"
                />
            )}
            <DateSelectButton
                startDate={filterValues.date_created_after}
                endDate={filterValues.date_created_before}
                handleDateChange={onDateTimeChange}
                initialButtonText={gettext('Past 2 weeks')}
                disabled={filterValues.pick_pack_batch_id_value !== ''}
            />
            <TextField
                id="order_id_value"
                type="search"
                margin="normal"
                variant="outlined"
                sx={{ width: 150, my: 0.25, ml: 1 }}
                value={filterValues.order_id_value}
                disabled={filterValues.pick_pack_batch_id_value !== ''}
                onChange={orderIdValueHandler}
                placeholder={gettext('Order/Tracking ID')}
                InputProps={{
                    style: { height: 30, fontSize: 13 },
                }}
                inputProps={{ style: { paddingLeft: 9 } }}
            />
            <TextField
                id="pick_pack_batch_id_value"
                type="search"
                margin="normal"
                variant="outlined"
                sx={{ width: 150, my: 0.25, ml: 1 }}
                value={filterValues.pick_pack_batch_id_value}
                onChange={pickPackBatchIdValueHandler}
                placeholder={gettext('Pick Pack Batch ID')}
                InputProps={{
                    style: { height: 30, fontSize: 13 },
                }}
                inputProps={{ style: { paddingLeft: 9 } }}
            />
            <TextField
                id="search"
                type="search"
                margin="normal"
                variant="outlined"
                sx={{ width: 150, my: 0.25, ml: 1 }}
                value={filterValues.search}
                disabled={filterValues.pick_pack_batch_id_value !== ''}
                onChange={searchHandler}
                placeholder={gettext('SKU/Product Name')}
                InputProps={{
                    style: { height: 30, fontSize: 13 },
                }}
                inputProps={{ style: { paddingLeft: 9 } }}
            />
            <Box sx={{ flex: '1 1 auto' }} />
            <Box sx={{ color: (theme) => theme.palette.text.secondary, display: 'flex' }}>
                <TablePagination
                    component="div"
                    count={count}
                    rowsPerPage={25}
                    page={page}
                    slotProps={{
                        actions: {
                            previousButton: {
                                'aria-label': gettext('Previous'),
                            },
                            nextButton: {
                                'aria-label': gettext('Next'),
                            },
                        },
                    }}
                    onPageChange={handleChangePage}
                    rowsPerPageOptions={[25]}
                />
            </Box>
        </Toolbar>
    )
}

export default class PickPackBatches extends PureComponent {
    constructor(props) {
        super(props)
        this.state = {
            rowsPerPage: 25,
            pickPackBatches: [],
            count: 0,
            page: 0,
            maxPageFetched: 0,
            loading: true,
            filterValues: {
                date_created_after: moment().subtract(13, 'days'),
                date_created_before: moment(),
                order_id_value: '',
                pick_pack_batch_id_value: '',
                search: '',
            },
            currentAbortController: null,
        }

        this.fetchPickPackBatchesDataDebounced = debounce(this.fetchPickPackBatchesData, 500)

        moment.locale(props.language)
    }

    getDataURL = () => {
        // In the future this should fetch batches assigned to the current user
        // The user on the PickPackBath may still be the account owner
        const { pickPackBatchesAPIURL, currentUserID } = this.props
        return pickPackBatchesAPIURL.replace('user_pk', currentUserID)
    }

    filtersToQueryString = (filters = false) => {
        let filtersToUse = filters || this.state.filterValues

        filtersToUse = filtersToUse.pick_pack_batch_id_value
            ? { pick_pack_batch_id_value: filtersToUse.pick_pack_batch_id_value }
            : filtersToUse

        return Object.entries(filtersToUse)
            .filter(([_fieldLookup, option]) => Boolean(option))
            .map(([fieldLookup, option]) => {
                if (moment.isMoment(option)) {
                    return `${fieldLookup}=${encodeURIComponent(option.format('YYYY-MM-DD'))}`
                } else {
                    return `${fieldLookup}=${encodeURIComponent(option.value || option)}`
                }
            })
            .join('&')
    }

    searchHandler = (event) => {
        const searchTerm = event.target.value
        const newFilters = { ...this.state.filterValues, search: searchTerm }
        this.setState({ filterValues: newFilters })
        this.fetchPickPackBatchesDataDebounced(this.getDataURL(), true, 0, newFilters)
    }

    orderIdValueHandler = (event) => {
        const orderIdValue = event.target.value
        const newFilters = { ...this.state.filterValues, order_id_value: orderIdValue }
        this.setState({ filterValues: newFilters })
        this.fetchPickPackBatchesDataDebounced(this.getDataURL(), true, 0, newFilters)
    }

    pickPackBatchIdValueHandler = (event) => {
        const pickPackBatchIDValue = event.target.value
        const newFilters = {
            ...this.state.filterValues,
            pick_pack_batch_id_value: pickPackBatchIDValue,
        }
        this.setState({ filterValues: newFilters })
        this.fetchPickPackBatchesDataDebounced(this.getDataURL(), true, 0, newFilters)
    }

    handleFilterChange = (field) => {
        return (option) => {
            const filterValues = { ...this.state.filterValues }
            if (option) {
                filterValues[field.lookup] = option
            } else {
                delete filterValues[field.lookup]
            }

            this.setState(
                {
                    filterValues,
                    page: 0,
                },
                () => {
                    this.fetchPickPackBatchesDataDebounced(this.getDataURL(), true, 0)
                }
            )
        }
    }

    handleDateTimeChange = (startDate, endDate) => {
        const filterValues = { ...this.state.filterValues }
        filterValues['date_created_after'] = startDate
        filterValues['date_created_before'] = endDate
        this.setState({ filterValues, page: 0 }, () => {
            this.fetchPickPackBatchesDataDebounced(this.getDataURL(), true, 0)
        })
    }

    componentDidMount(_prevProps) {
        this.fetchPickPackBatchesDataDebounced(this.getDataURL(), false, 0)
    }

    componentWillUnmount() {
        this.state.currentAbortController?.abort()
    }

    fetchPickPackBatchesData = async (url, replace, page = null, filtersToUse = false) => {
        this.state.currentAbortController?.abort()

        const abortController = new AbortController()
        this.setState({
            currentAbortController: abortController,
            loading: true,
        })

        if (!url.includes('?')) {
            const queryString = this.filtersToQueryString(filtersToUse)
            if (queryString) {
                url += '?' + queryString
            }
        }

        try {
            const res = await fetch(url, { signal: abortController.signal })
            if (!res.ok) {
                throw new Error('Failed to fetch pick pack batches data')
            }

            const data = await res.json()
            let pickPackBatches
            if (replace) {
                pickPackBatches = data.results
            } else {
                pickPackBatches = this.state.pickPackBatches.concat(data.results)
            }

            const newState = {
                count: data.count,
                next: data.next,
                previous: data.previous,
                pickPackBatches: pickPackBatches,
            }

            if (page === 0) {
                newState['page'] = 0
                newState['maxPageFetched'] = 0
            } else if (page > 0) {
                newState['page'] = page
            }

            this.setState(newState)
        } catch (error) {
            if (error.name !== 'AbortError') {
                console.error('Error fetching pick pack batches:', error)
            }
        } finally {
            this.setState((prevState) => ({
                ...prevState,
                loading: false,
                currentAbortController:
                    prevState.currentAbortController === abortController
                        ? null
                        : prevState.currentAbortController,
            }))
        }
    }

    handleChangePage = (_event, page) => {
        const pageChangeURL = page > this.state.page ? this.state.next : this.state.previous
        const needsFetch = page > this.state.maxPageFetched

        if (needsFetch) {
            this.setState({ maxPageFetched: page }, () => {
                this.fetchPickPackBatchesData(pageChangeURL, false, page)
            })
        } else {
            this.setState({ page })
        }
    }

    render() {
        const { staticRoot } = this.props
        const { rowsPerPage, page, loading, pickPackBatches, count, filterValues } = this.state
        const emptyRows =
            rowsPerPage -
            Math.min(rowsPerPage, this.state.pickPackBatches.length - page * rowsPerPage)

        const displayedPickPackBatches = this.state.pickPackBatches.slice(
            page * rowsPerPage,
            page * rowsPerPage + rowsPerPage
        )

        return (
            <Paper sx={{ width: '100%', height: '100%' }}>
                <PickPackBatchesTableToolbar
                    handleChangePage={this.handleChangePage}
                    count={count}
                    page={page}
                    loading={loading}
                    filterValues={filterValues}
                    searchHandler={this.searchHandler}
                    orderIdValueHandler={this.orderIdValueHandler}
                    pickPackBatchIdValueHandler={this.pickPackBatchIdValueHandler}
                    onDateTimeChange={this.handleDateTimeChange}
                />
                <Box sx={{ overflowX: 'auto' }}>
                    <Table sx={{ width: '100%' }} aria-labelledby="tableTitle">
                        <PickPackBatchesTableHead />
                        <TableBody>
                            {!loading && pickPackBatches.length == 0 && (
                                <TableRow>
                                    <TableCell
                                        colSpan={columns.length}
                                        sx={{ textAlign: 'center', padding: 9 }}
                                    >
                                        <img src={staticRoot + 'web/img/order.png'} />
                                        <Typography color="text.secondary" variant="subtitle1">
                                            {gettext('No pick lists to display')}
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                            {displayedPickPackBatches.map((pickPackBatch) => {
                                const pickPackBatchLink = getPath('pickPackBatch', {
                                    pick_pack_batch_id: pickPackBatch.id,
                                })

                                return (
                                    <TableRow hover tabIndex={-1} key={pickPackBatch.id}>
                                        <TableCell>
                                            <Link to={pickPackBatchLink}>
                                                <Typography variant="body2">
                                                    {pickPackBatch.id}
                                                </Typography>
                                            </Link>
                                        </TableCell>
                                        <TableCell>
                                            <Link to={pickPackBatchLink}>
                                                <Typography variant="body2">
                                                    {pickPackBatch.location.name}
                                                </Typography>
                                            </Link>
                                        </TableCell>
                                        <TableCell>
                                            <Link to={pickPackBatchLink}>
                                                <Typography variant="body2">
                                                    {moment(pickPackBatch.date_created).format(
                                                        'D MMM HH:mm'
                                                    )}
                                                </Typography>
                                            </Link>
                                        </TableCell>
                                        <TableCell>
                                            <Link to={pickPackBatchLink}>
                                                <Typography variant="body2">
                                                    {pickPackBatch.channel_names?.join(', ')}
                                                </Typography>
                                            </Link>
                                        </TableCell>
                                        <TableCell>
                                            <Link to={pickPackBatchLink}>
                                                <Typography variant="body2">
                                                    {pickPackBatch.num_fulfillments}
                                                </Typography>
                                            </Link>
                                        </TableCell>
                                        <TableCell>
                                            <Link to={pickPackBatchLink}>
                                                <Typography variant="body2">
                                                    {pickPackBatch.num_items}
                                                </Typography>
                                            </Link>
                                        </TableCell>
                                        <TableCell>
                                            <Link to={pickPackBatchLink}>
                                                <Box sx={{ display: 'flex' }}>
                                                    <Box
                                                        sx={{
                                                            backgroundColor:
                                                                STATUS_INDICATOR_COLORS[
                                                                    pickPackBatch.status
                                                                ],
                                                            width: 16,
                                                            height: 16,
                                                            borderRadius: 2,
                                                            mr: 1.25,
                                                        }}
                                                    />
                                                    <Typography variant="body2">
                                                        {pickPackBatch.status_display_name}
                                                    </Typography>
                                                </Box>
                                            </Link>
                                        </TableCell>
                                    </TableRow>
                                )
                            })}
                            {emptyRows > 0 && (
                                <TableRow style={{ height: 49 * emptyRows }}>
                                    <TableCell colSpan={6} />
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </Box>
                <TablePagination
                    component="div"
                    count={count}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    rowsPerPageOptions={[25]}
                    slotProps={{
                        actions: {
                            previousButton: {
                                'aria-label': gettext('Previous'),
                            },
                            nextButton: {
                                'aria-label': gettext('Next'),
                            },
                        },
                    }}
                    onPageChange={this.handleChangePage}
                />
            </Paper>
        )
    }
}
